import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  Input,
  RadioGroup,
  RadioGroupItem,
  SelectItem,
  SimpleSelect,
  Step,
  Steps,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@solinkcorp/solink-ui';
import {
  MAX_TIMESTAMP_OFFSET,
  MIN_TIMESTAMP_OFFSET,
  TIME_TARGETS,
  type UseSpotCheckForm,
} from 'components/SpotCheckPanel/hooks/useSpotCheckForm';
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Info,
  ListChecks,
  MessageSquare,
  SlidersHorizontal,
} from 'lucide-react';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import LocaleUtil from 'utils/LocaleUtil';
import type ReportUtil from 'utils/ReportUtil';
import { getValidationErrorMessage } from 'utils/yupUtils';
import StatesList from '../SpotCheckMainContent/SpotCheckFormView/Aspen/StatesList';

interface SpotCheckFormFieldsProps {
  spotCheckForm: UseSpotCheckForm;
  reportOptions: { value: string; label: string }[];
  selectedReportOption: string | undefined;
  onReportChange: (templateId: string) => void;
  reportSelectErrorText?: string;
  reportSelectWarningText?: string;
  isEventSpotCheck: boolean;
  isResponseFormatCollapsed: boolean;
  setIsResponseFormatCollapsed: (next: boolean) => void;
  aiChatAnswerFormat: string;
  setAiChatAnswerFormat: (value: string) => void;
  scheduleStep?: React.ReactNode;
}

const spotCheckFormStepsClassName =
  'tw w-full min-w-0 [&_[data-slot=step]>[data-slot=card]]:overflow-visible [&_[data-slot=step]>[data-slot=card]]:border-border-muted [&_[data-slot=step]>[data-slot=card]]:bg-background-surface [&_[data-slot=step]>[data-slot=card]]:shadow-elevation-1 [&_[data-slot=card-content]]:min-w-0 [&_[data-slot=card-content]]:overflow-visible';

function FieldLabel({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="tw mb-1.5 flex items-center gap-1">
      <span className="text-foreground-primary label-sm">{children}</span>
      {hint ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="tw inline-flex text-foreground-muted">
              <Info className="size-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="tw max-w-xs text-left">
            {hint}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

export default function SpotCheckFormFields(props: SpotCheckFormFieldsProps) {
  const {
    spotCheckForm,
    reportOptions,
    selectedReportOption,
    onReportChange,
    reportSelectErrorText,
    reportSelectWarningText,
    isEventSpotCheck,
    isResponseFormatCollapsed,
    setIsResponseFormatCollapsed,
    aiChatAnswerFormat,
    setAiChatAnswerFormat,
    scheduleStep,
  } = props;
  const locale = useLocale();
  const {
    aiChatPrompt,
    setAiChatPrompt,
    spotCheckValidationError,
    eventTimeTarget,
    setEventTimeTarget,
    eventTimestampOffset,
    setEventTimestampOffset,
  } = spotCheckForm;

  const aiChatPromptErrorText = getValidationErrorMessage(spotCheckValidationError, 'aiChatPrompt');
  const [timestampOffsetDraft, setTimestampOffsetDraft] = React.useState<string | null>(null);

  React.useEffect(() => {
    setTimestampOffsetDraft(null);
  }, [eventTimestampOffset]);

  const handleTimestampOffsetChange = (raw: string) => {
    if (raw === '-') {
      setTimestampOffsetDraft(raw);
      return;
    }
    if (raw === '') {
      setTimestampOffsetDraft(null);
      setEventTimestampOffset(0);
      return;
    }
    const next = Number(raw);
    if (!Number.isFinite(next) || next < MIN_TIMESTAMP_OFFSET || next > MAX_TIMESTAMP_OFFSET) {
      return;
    }
    setTimestampOffsetDraft(null);
    setEventTimestampOffset(next);
  };

  const timeTargets = [
    { value: TIME_TARGETS.START, label: locale.scheduledAIChat.start },
    { value: TIME_TARGETS.END, label: locale.scheduledAIChat.end },
  ];

  return (
    <Steps className={spotCheckFormStepsClassName}>
      <Step
        icon={<MessageSquare className="size-4" />}
        title={locale.scheduledAIChat.prompt}
        className="tw min-w-0 max-w-full"
      >
        <div className="tw flex min-w-0 flex-col gap-2">
          <p className="text-foreground-secondary body-sm">
            {locale.scheduledAIChat.promptTooltip}
          </p>
          <Textarea
            name="prompt"
            rows={6}
            value={aiChatPrompt}
            onChange={(event) => setAiChatPrompt(event.target.value)}
            aria-label={`${locale.scheduledAIChat.prompt} *`}
            aria-invalid={!!aiChatPromptErrorText}
            required
            className="tw min-h-[120px] w-full"
          />
          {aiChatPromptErrorText ? (
            <p className="tw text-status-red caption">{aiChatPromptErrorText}</p>
          ) : null}
        </div>
      </Step>

      <Step
        icon={<ListChecks className="size-4" />}
        title={locale.scheduledAIChat.statesLabel}
        className="tw min-w-0 max-w-full"
      >
        <div className="tw flex min-w-0 flex-col gap-3">
          <StatesList spotCheckForm={spotCheckForm} hideSectionTitle />
          {aiChatAnswerFormat ? (
            <div className="tw rounded-md border border-border-muted">
              <button
                type="button"
                className="body-sm-strong tw flex w-full items-center justify-between px-3 py-2 text-left text-foreground-primary"
                onClick={() => setIsResponseFormatCollapsed(!isResponseFormatCollapsed)}
              >
                <span>{locale.scheduledAIChat.additionalResponseContext}</span>
                {isResponseFormatCollapsed ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronUp className="size-4" />
                )}
              </button>
              {!isResponseFormatCollapsed ? (
                <div className="tw border-t border-border-muted p-3">
                  <FieldLabel>{locale.scheduledAIChat.responseFormat}</FieldLabel>
                  <Textarea
                    name="response-format"
                    rows={4}
                    value={aiChatAnswerFormat}
                    onChange={(event) => setAiChatAnswerFormat(event.target.value)}
                    className="tw w-full shadow-none focus-visible:ring-0"
                  />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Step>

      {isEventSpotCheck ? (
        <Step
          icon={<SlidersHorizontal className="size-4" />}
          title={locale.scheduledAIChat.reportsSelectorText}
          className="tw min-w-0 max-w-full"
        >
          <div className="tw flex min-w-0 flex-col gap-4">
            <div>
              <FieldLabel hint={locale.scheduledAIChat.reportsSelectorInfo}>
                {locale.scheduledAIChat.reportsSelectorText}
              </FieldLabel>
              <SimpleSelect
                id="spot-check-report-select"
                value={selectedReportOption ?? ''}
                onChange={(value) => onReportChange(String(value))}
                placeholder={locale.general.search}
                className="tw w-full"
                aria-invalid={!!reportSelectErrorText}
              >
                {reportOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SimpleSelect>
              {reportSelectWarningText ? (
                <p className="tw mt-1 text-foreground-secondary caption">
                  {reportSelectWarningText}
                </p>
              ) : null}
              {reportSelectErrorText ? (
                <p className="tw mt-1 text-status-red caption">{reportSelectErrorText}</p>
              ) : null}
            </div>

            <div>
              <FieldLabel hint={locale.scheduledAIChat.timeTargetsInfo}>
                {locale.scheduledAIChat.timeTargetsLabel}
              </FieldLabel>
              <RadioGroup
                value={eventTimeTarget}
                onValueChange={(value) => setEventTimeTarget(value as typeof eventTimeTarget)}
                disabled={!selectedReportOption}
                className="tw flex flex-row flex-wrap gap-4"
              >
                {timeTargets.map((option) => (
                  <label
                    key={option.value}
                    className="tw flex cursor-pointer items-center gap-2 text-foreground-primary body-sm"
                  >
                    <RadioGroupItem value={option.value} />
                    {option.label}
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div>
              <FieldLabel hint={locale.scheduledAIChat.timestampOffsetInfo}>
                {locale.scheduledAIChat.timestampOffsetLabel}
              </FieldLabel>
              <Input
                type="text"
                inputMode="numeric"
                name="timestamp-offset"
                value={timestampOffsetDraft ?? String(eventTimestampOffset)}
                disabled={!selectedReportOption}
                onChange={(event) => handleTimestampOffsetChange(event.target.value)}
                onBlur={() => setTimestampOffsetDraft(null)}
                className="tw max-w-[8rem]"
              />
            </div>
          </div>
        </Step>
      ) : null}

      {scheduleStep ? (
        <Step
          icon={<CalendarClock className="size-4" />}
          title={locale.alarms.schedule}
          className="tw min-w-0 max-w-full"
        >
          <div className="tw flex min-w-0 flex-col gap-4">{scheduleStep}</div>
        </Step>
      ) : null}
    </Steps>
  );
}

export function getReportSelectMessages(
  locale: LocaleDefinition,
  isEventSpotCheck: boolean,
  selectedEventReport: ReportUtil | undefined,
  isEventsEstimateBelowLimit: boolean,
  totalEventsInLast30Days: number | undefined,
  eventsMonthlyQuotaLimit: number | undefined,
) {
  const reportSelectErrorText =
    !isEventSpotCheck || !selectedEventReport || isEventsEstimateBelowLimit
      ? undefined
      : LocaleUtil.getLocalizedStr({
          string: locale.scheduledAIChat.eventSpotCheckEstimateExceedsQuota,
          replacements: { number: totalEventsInLast30Days, quota: eventsMonthlyQuotaLimit },
        });

  const reportSelectWarningText =
    !isEventSpotCheck || !selectedEventReport || doesReportHaveLocationFilter(selectedEventReport)
      ? undefined
      : locale.scheduledAIChat.eventSpotCheckNoLocationFilterWarning;

  return { reportSelectErrorText, reportSelectWarningText };
}

function doesReportHaveLocationFilter(report: ReportUtil | undefined): boolean {
  const filters = report?.query?.query?.bool?.filter;
  if (!Array.isArray(filters)) return false;

  return filters.some((filter) => {
    const terms = filter['terms'] ?? {};
    const term = filter['term'] ?? {};

    return (
      (Array.isArray(terms.locationId) && terms.locationId.length > 0) ||
      Boolean(term.locationId) ||
      (Array.isArray(terms.locationTags) && terms.locationTags.length > 0) ||
      Boolean(term.locationTags)
    );
  });
}
